// @catalog F.048
// Resultados de la ingesta Amazon: líneas importadas del reporte de pedidos
// de Seller Central, con filtro por mes y paginación. Sin economía del canal:
// el reporte no trae comisiones (ver entidad marketplace_amazon_order_items).
import { useState } from "react";
import {
  Box,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  BodyCell,
  BodyRow,
  HeaderCell,
  TableShell,
} from "../analytics/TableCells";
import MercadoLibreImportService from "../../services/mercadoLibreImport.service";

const fmt = MercadoLibreImportService.formatCurrency;
const PAGE_SIZE = 50;

export function AmazonPedidosBlock(): JSX.Element {
  const [month, setMonth] = useState("");
  const [page, setPage] = useState(1);

  const itemsQuery = useQuery({
    queryKey: ["amazon-items", month, page],
    queryFn: () =>
      MercadoLibreImportService.getAmazonItems({
        month: month || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: month === "" || /^\d{4}-\d{2}$/.test(month),
  });
  const data = itemsQuery.data;
  const pageCount = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        useFlexGap
        sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
      >
        <Typography
          component="h2"
          sx={{ fontSize: "16px", fontWeight: 600, color: "var(--ink-1)" }}
        >
          Líneas de pedido Amazon
        </Typography>
        <Box sx={{ flex: 1 }} />
        {data && (
          <Typography sx={{ fontSize: "12.5px", color: "var(--ink-3)" }}>
            {data.total.toLocaleString()} líneas
          </Typography>
        )}
        <TextField
          size="small"
          type="month"
          label="Mes (todos si vacío)"
          value={month}
          onChange={(e) => {
            setMonth(e.target.value);
            setPage(1);
          }}
          sx={{ width: 190 }}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      <TableShell maxHeight="max(360px, calc(100vh - 295px))">
        <Box component="thead">
          <Box component="tr">
            <HeaderCell>Fecha</HeaderCell>
            <HeaderCell>Pedido</HeaderCell>
            <HeaderCell>SKU</HeaderCell>
            <HeaderCell>Producto</HeaderCell>
            <HeaderCell align="right">Cant.</HeaderCell>
            <HeaderCell align="right">Precio</HeaderCell>
            <HeaderCell align="right">IVA</HeaderCell>
            <HeaderCell align="right">Envío</HeaderCell>
            <HeaderCell>Destino</HeaderCell>
            <HeaderCell>Canal</HeaderCell>
          </Box>
        </Box>
        <Box component="tbody">
          {(data?.rows ?? []).length === 0 && (
            <Box component="tr">
              <BodyCell muted>
                {itemsQuery.isLoading
                  ? "Cargando…"
                  : "Sin líneas para el filtro."}
              </BodyCell>
            </Box>
          )}
          {(data?.rows ?? []).map((r) => (
            <BodyRow key={r.orderItemId}>
              <BodyCell muted>{r.purchaseDate}</BodyCell>
              <BodyCell>
                <Tooltip arrow title={`línea ${r.orderItemId}`}>
                  <span>{r.orderId}</span>
                </Tooltip>
              </BodyCell>
              <BodyCell muted>{r.sku ?? "—"}</BodyCell>
              <BodyCell>
                <Tooltip arrow title={r.productName ?? ""}>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      maxWidth: 260,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      verticalAlign: "bottom",
                    }}
                  >
                    {r.productName ?? "—"}
                  </Box>
                </Tooltip>
              </BodyCell>
              <BodyCell align="right">{r.quantity ?? "—"}</BodyCell>
              <BodyCell align="right">{fmt(r.itemPrice)}</BodyCell>
              <BodyCell align="right">{fmt(r.itemTax)}</BodyCell>
              <BodyCell align="right">
                {fmt(r.shippingPrice + r.shippingTax)}
              </BodyCell>
              <BodyCell muted>
                {[r.shipCity, r.shipState].filter(Boolean).join(", ") || "—"}
              </BodyCell>
              <BodyCell muted>{r.salesChannel ?? "—"}</BodyCell>
            </BodyRow>
          ))}
        </Box>
      </TableShell>
      {pageCount > 1 && (
        <Stack alignItems="center" sx={{ mt: 1.5 }}>
          <Pagination
            size="small"
            count={pageCount}
            page={page}
            onChange={(_e, p) => setPage(p)}
          />
        </Stack>
      )}
    </Box>
  );
}
